/**
 * @file: useHandleLoginResult.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import type { AuthState } from '@euler/app/flows/auth/context';
import {
  kAgentInfo,
  kAuthProvider,
  kAuthResult,
} from '@euler/functions/constants';
import { AnalyticEvents } from '@euler/generated/AnalyticEvents';
import { AuthenticatedUserInfo } from '@euler/model';
import { AuthProvider, AuthenticatorResult } from '@euler/model/auth';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useCallback } from 'react';
import { BehaviorSubject } from 'rxjs';

export const useHandleLoginResult = (
  authState$: BehaviorSubject<AuthState>,
) => {
  const { defaultTokenService, defaultStorageService } = useServiceFactory();

  return useCallback(
    async <T extends AuthProvider>(
      providerType: T['type'],
      result: AuthenticatorResult<T>,
    ) => {
      try {
        await defaultTokenService.setToken(result.token);
      } catch (e) {}

      if (result.refreshToken) {
        await defaultTokenService
          .setRefreshToken(result.refreshToken)
          .catch(() => {});
      }

      if (result.refreshTokenExpiresIn) {
        await defaultTokenService
          .setRefreshTokenExpiry(
            Date.now() + result.refreshTokenExpiresIn * 1000,
          )
          .catch(() => {});
      }

      AnalyticEvents.default.userLoginSuccess({
        loginMethod: providerType,
        userId: result.user.uid,
      });

      const authenticatedUserInfo: AuthenticatedUserInfo = {
        ...result.results,
      };

      defaultStorageService
        .set(kAuthResult, authenticatedUserInfo)
        .catch(onErrorIgnore);

      defaultStorageService
        .set(kAgentInfo, authenticatedUserInfo.agent)
        .catch(onErrorIgnore);

      defaultStorageService
        .set(kAuthProvider, providerType)
        .catch(onErrorIgnore);

      authState$.next({
        isAuthenticated: true,
        authenticatedUserInfo: authenticatedUserInfo,
        provider: providerType,
      });
    },
    [authState$, defaultStorageService, defaultTokenService],
  );
};
